import React from 'react';
import { Link } from 'react-router-dom';
import { CaretLeft, DeviceMobile, Share, PlusSquare, Bell, Question } from '@phosphor-icons/react';

const Step = ({ n, title, children }) => (
  <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', alignItems: 'flex-start' }}>
    <div style={{
      flexShrink: 0, width: '36px', height: '36px', borderRadius: '50%',
      background: '#002FA7', color: 'white', display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontWeight: '800', fontSize: '1rem'
    }}>{n}</div>
    <div style={{ flex: 1, paddingTop: '4px' }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '1.05rem', fontWeight: '700', color: '#1E2030' }}>{title}</h3>
      <div style={{ color: '#5E5F66', lineHeight: '1.55', fontSize: '0.95rem' }}>{children}</div>
    </div>
  </div>
);

const Section = ({ icon: Icon, title, color, children }) => (
  <div className="card" style={{ padding: '24px 22px', marginBottom: '18px', borderLeft: `4px solid ${color}` }}>
    <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 16px', fontSize: '1.25rem', color: '#1E2030' }}>
      <Icon size={24} weight="bold" color={color} /> {title}
    </h2>
    {children}
  </div>
);

const MobileHelpPage = () => (
  <div data-testid="mobile-help-page" style={{ maxWidth: '760px', margin: '0 auto', padding: '20px 16px' }}>
    <Link to="/dashboard" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#5E5F66', textDecoration: 'none', fontSize: '0.9rem', marginBottom: '12px' }}>
      <CaretLeft size={16} /> Retour au tableau de bord
    </Link>

    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
      <DeviceMobile size={56} weight="duotone" color="#002FA7" />
      <h1 style={{ margin: '8px 0 4px', fontSize: '1.8rem', fontWeight: '800', color: '#1E2030' }}>Installer BATI-BOYS sur iPhone</h1>
      <p style={{ color: '#5E5F66', margin: 0 }}>Guide pas-à-pas pour une utilisation native, même hors-ligne</p>
    </div>

    <Section icon={PlusSquare} title="Ajouter à l'écran d'accueil (PWA)" color="#002FA7">
      <Step n={1} title="Ouvrez BATI-BOYS dans Safari">
        L'installation PWA fonctionne uniquement avec <b>Safari</b> sur iPhone/iPad (pas Chrome ni Firefox).
        Tapez l'adresse de l'application dans la barre d'adresse.
      </Step>
      <Step n={2} title="Appuyez sur le bouton Partager">
        En bas de Safari, touchez l'icône <Share size={16} weight="bold" style={{ verticalAlign: 'middle' }} /> (carré avec flèche vers le haut).
      </Step>
      <Step n={3} title="Sélectionnez « Sur l'écran d'accueil »">
        Dans la liste d'actions, faites défiler et touchez <b>« Sur l'écran d'accueil »</b> (ou « Add to Home Screen »).
      </Step>
      <Step n={4} title="Confirmez">
        Touchez <b>« Ajouter »</b> en haut à droite. L'icône BATI-BOYS apparaît sur votre écran d'accueil.
      </Step>
      <Step n={5} title="Lancez l'application">
        Ouvrez BATI-BOYS depuis l'icône : l'app se lance en plein écran, sans la barre Safari, comme une vraie application native.
      </Step>
    </Section>

    <Section icon={Bell} title="Activer les notifications push" color="#2E7D32">
      <div style={{ background: '#FFF3E0', padding: '12px 14px', borderRadius: '6px', marginBottom: '18px', fontSize: '0.88rem', color: '#E65100' }}>
        <b>Important</b> : les notifications push sur iPhone nécessitent <b>iOS 16.4 ou plus récent</b>.
        Vérifiez votre version dans <i>Réglages → Général → Informations</i>.
      </div>
      <Step n={1} title="Ouvrez BATI-BOYS depuis l'écran d'accueil">
        Les notifications ne fonctionnent <b>que</b> si l'app a été ajoutée à l'écran d'accueil (étape précédente).
      </Step>
      <Step n={2} title="Connectez-vous à votre compte">
        Saisissez votre email et votre mot de passe comme d'habitude.
      </Step>
      <Step n={3} title="Activez les notifications">
        Rendez-vous dans <b>Réglages / Notifications push</b> dans l'app et touchez <b>« Activer »</b>.
        iOS affichera une demande de permission — touchez <b>« Autoriser »</b>.
      </Step>
      <Step n={4} title="Testez">
        Une notification de test peut être déclenchée depuis les réglages. Si elle arrive, tout est OK !
      </Step>
    </Section>

    <Section icon={Question} title="Problèmes fréquents" color="#EF6C00">
      <div style={{ marginBottom: '14px' }}>
        <div style={{ fontWeight: '700', color: '#1E2030', marginBottom: '4px' }}>L'option « Sur l'écran d'accueil » n'apparaît pas</div>
        <div style={{ color: '#5E5F66', fontSize: '0.9rem' }}>Vous utilisez probablement Chrome, Firefox ou Edge. Réouvrez l'app avec <b>Safari</b>.</div>
      </div>
      <div style={{ marginBottom: '14px' }}>
        <div style={{ fontWeight: '700', color: '#1E2030', marginBottom: '4px' }}>Les notifications ne s'affichent pas</div>
        <div style={{ color: '#5E5F66', fontSize: '0.9rem' }}>Vérifiez que : (1) iOS 16.4+, (2) app lancée depuis l'écran d'accueil (pas depuis Safari), (3) notifications autorisées dans <i>Réglages iOS → BATI-BOYS → Notifications</i>.</div>
      </div>
      <div style={{ marginBottom: '14px' }}>
        <div style={{ fontWeight: '700', color: '#1E2030', marginBottom: '4px' }}>L'app ne se connecte plus</div>
        <div style={{ color: '#5E5F66', fontSize: '0.9rem' }}>Fermez complètement l'app (double-clic + glissement vers le haut) puis relancez-la. Si le problème persiste, désinstallez et réinstallez depuis Safari.</div>
      </div>
      <div>
        <div style={{ fontWeight: '700', color: '#1E2030', marginBottom: '4px' }}>Besoin d'aide ?</div>
        <div style={{ color: '#5E5F66', fontSize: '0.9rem' }}>Contactez votre administrateur BATI-BOYS — il pourra vous guider en direct ou réinitialiser votre compte.</div>
      </div>
    </Section>

    <div style={{ textAlign: 'center', color: '#9E9EA7', fontSize: '0.8rem', marginTop: '26px' }}>
      BATI-BOYS — Guide d'installation iPhone · Conforme CNIL & RGPD
    </div>
  </div>
);

export default MobileHelpPage;
